import { Upload, Video } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BottomPayment from '@/domains/mobile-web/components/BottomPayment/BottomPayment';
import BottomSheetMobile, {
  type SheetOption,
  SheetOptionList,
} from '@/domains/mobile-web/components/BottomSheetMobile/BottomSheetMobile';
import CardRefundOrder, {
  RefundNote,
} from '@/domains/mobile-web/components/CardRefundOrder/CardRefundOrder';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import PickerFieldMobile from '@/domains/mobile-web/components/PickerFieldMobile/PickerFieldMobile';
import TextAreaMobile from '@/domains/mobile-web/components/TextAreaMobile/TextAreaMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
  REFUND_BANK_OPTIONS,
  REFUND_NOTES,
  REFUND_PRODUCT_BUNDLING,
  REFUND_PRODUCT_LEBAH,
  REFUND_REASONS,
  REFUND_STATIC,
} from '@/domains/mobile-web/constant/refund.constant';
import { cn } from '@/lib/utils';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';

const SectionTitle = ({ title, required = false }: { title: string; required?: boolean }) => (
  <h3 className="title2medium text-black">
    {title}
    {required && <span className="ml-0.5 text-text-danger-500">*</span>}
  </h3>
);

const Refund = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isReview = searchParams.get('status') === 'review';
  const readOnly = isReview;

  const [selectedIds, setSelectedIds] = useState<string[]>([REFUND_PRODUCT_LEBAH.id]);
  const [reason, setReason] = useState<string>(isReview ? REFUND_STATIC.review.reason : '');

  // Pilih bank lewat bottom sheet, sama seperti halaman /store/bank
  const [isBankSheetOpen, setIsBankSheetOpen] = useState(false);
  const [bankSearch, setBankSearch] = useState('');
  const [selectedBank, setSelectedBank] = useState<SheetOption | null>(
    isReview
      ? (REFUND_BANK_OPTIONS.find((b) => b.value === REFUND_STATIC.review.bank) ?? null)
      : null
  );
  const filteredBanks = REFUND_BANK_OPTIONS.filter((bank) =>
    bank.label.toLowerCase().includes(bankSearch.toLowerCase())
  );
  const handleSelectBank = (option: SheetOption) => {
    setSelectedBank(option);
    setIsBankSheetOpen(false);
  };

  const toggleProduct = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));

  // Form menampilkan semua produk pesanan; review hanya produk yang diajukan.
  const products = isReview
    ? [REFUND_PRODUCT_LEBAH]
    : [REFUND_PRODUCT_BUNDLING, REFUND_PRODUCT_LEBAH];
  // Checkbox hanya perlu jika ada lebih dari satu produk; satu produk otomatis diajukan.
  const selectable = !isReview && products.length > 1;
  const estimate = useMemo(
    () =>
      products
        .filter((p) => !selectable || selectedIds.includes(p.id))
        .reduce((sum, p) => sum + p.product.price * p.quantity, 0),
    [products, selectedIds, selectable]
  );

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" onBack={() => navigate(-1)} title="Pengajuan Refund" />
      </HeaderMobile>

      <WrapperMobile className="mb-[130px]">
        <CardRefundOrder
          date={REFUND_STATIC.date}
          status={isReview ? 'review' : 'arrived'}
          code={isReview ? REFUND_STATIC.refundCode : REFUND_STATIC.invoice}
          products={products}
          selectable={selectable}
          selectedIds={selectedIds}
          onToggle={toggleProduct}
          note={selectable ? REFUND_NOTES.unselected : undefined}
        />

        {/* Alasan refund */}
        <article className="shadowCard flex w-full flex-col gap-3 bg-white p-3">
          <SectionTitle title="Alasan Refund" required />
          <div className="flex flex-wrap gap-2">
            {REFUND_REASONS.map((item) => {
              const active = reason === item;
              return (
                <button
                  key={item}
                  type="button"
                  disabled={readOnly}
                  onClick={() => setReason(item)}
                  className={cn(
                    'rounded-[8px] border px-3 py-1.5 reg12 transition disabled:cursor-default',
                    active
                      ? 'border-text-blue-100 bg-[#007AFF1F] text-text-blue-100'
                      : 'border-transparent bg-[#F5F5F5] text-text-grey'
                  )}
                >
                  {item}
                </button>
              );
            })}
          </div>
          <TextAreaMobile
            label="Deskripsi"
            name="description"
            placeholder="Jelaskan masalah yang kamu alami"
            rows={4}
            disabled={readOnly}
            defaultValue={isReview ? REFUND_STATIC.review.description : ''}
          />
        </article>

        <article className="shadowCard flex w-full flex-col gap-3 bg-white p-3">
          <SectionTitle title="Video Bukti" required />
          <div className="flex items-center gap-3">
            {isReview && REFUND_PRODUCT_LEBAH.product.frontCover ? (
              <figure className="h-[80px] w-[96px] shrink-0 overflow-hidden rounded-[12px]">
                <img
                  src={REFUND_PRODUCT_LEBAH.product.frontCover}
                  alt="Video bukti"
                  className="h-full w-full object-cover"
                />
              </figure>
            ) : (
              <div className="flex h-[80px] w-[96px] shrink-0 items-center justify-center rounded-[12px] border border-dashed border-blue-cs-200 bg-[#F3F8FF] text-text-blue-100">
                <Video size={24} strokeWidth={1.8} />
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p className="reg12 text-text-grey">Rekam kondisi buku atau video unboxing.</p>
              <Button
                type="button"
                disabled={readOnly}
                customButton="h-9! w-fit rounded-[8px] px-3 text-[14px]"
                className={cn(
                  readOnly
                    ? 'bg-[#D9D9D9] text-white'
                    : 'bg-text-blue-100 text-white hover:bg-text-blue-100/90'
                )}
              >
                <Upload size={14} strokeWidth={2} />
                Unggah Video
              </Button>
            </div>
          </div>
        </article>

        {/* Dana refund */}
        <article className="shadowCard flex w-full flex-col gap-3 bg-white p-3">
          <SectionTitle title="Dana Refund" />
          <PickerFieldMobile
            label="Pilih Bank"
            name="bank"
            required
            placeholder="Pilih Bank"
            value={selectedBank?.label}
            disabled={readOnly}
            onClick={() => setIsBankSheetOpen(true)}
          />
          <InputMobile
            label="Nomor Rekening"
            name="accountNumber"
            required
            inputMode="numeric"
            placeholder="Masukkan nomor rekening"
            disabled={readOnly}
            defaultValue={isReview ? REFUND_STATIC.review.accountNumber : ''}
          />
          <InputMobile
            label="Nama Pemilik Rekening"
            name="accountName"
            required
            placeholder="Masukkan nama pemilik rekening"
            disabled={readOnly}
            defaultValue={isReview ? REFUND_STATIC.review.accountName : ''}
          />
        </article>

        <RefundNote text={REFUND_NOTES.reviewProcess} />
      </WrapperMobile>

      <BottomPayment
        title="Estimasi Refund"
        textButton="Ajukan Refund"
        totalPrice={estimate}
        disabled={readOnly || (selectable && selectedIds.length === 0)}
      />

      <BottomSheetMobile
        open={isBankSheetOpen}
        onClose={() => setIsBankSheetOpen(false)}
        title="Pilih Bank"
        searchable
        searchValue={bankSearch}
        onSearchChange={setBankSearch}
        searchPlaceholder="Cari nama bank"
      >
        <SheetOptionList
          options={filteredBanks}
          value={selectedBank?.value}
          onSelect={handleSelectBank}
        />
      </BottomSheetMobile>
    </main>
  );
};

export default Refund;
